import { useMemo } from "react";
import RxWatermark from "@/components/RxWatermark";

function safe(v, fb = "") { return v === undefined || v === null ? fb : v; }
function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch { return String(iso); }
}

const PAPER = {
  A4: "size-A4", Letter: "size-Letter", Half: "size-Half",
};

export default function ReferralPreview({ referral, settings }) {
  const s = settings || {};
  const doc = useMemo(() => referral || {}, [referral]);
  const patient = doc.patient_snapshot || {};
  const doctor = doc.referring_doctor_snapshot || {};
  const isMulti = (doctor.layout_template || s.layout_template) === "multi_clinic";
  const clinics = Array.isArray(doctor.clinics) ? doctor.clinics : [];
  const affiliations = Array.isArray(doctor.affiliations) && doctor.affiliations.length
    ? doctor.affiliations
    : (Array.isArray(s.affiliations) ? s.affiliations : []);
  const paperCls = PAPER[s.paper_size] || "size-A4";
  const pageStyle = s.paper_size === "Custom" ? { width: `${s.custom_width || 210}mm`, minHeight: `${s.custom_height || 297}mm` } : {};
  const autoFit = s.auto_fit !== false;

  // Doctor letterhead — lives in the table <thead> so the browser repeats it on
  // every printed page when a long clinical summary flows onto a second sheet.
  const header = isMulti ? (
    <div className="pb-3 border-b-2 border-slate-800" style={{ display: "grid", gridTemplateColumns: "42% 58%", columnGap: "6mm", alignItems: "flex-start" }}>
      <div>
        <div style={{ fontFamily: "Work Sans", fontWeight: 800, fontSize: "15pt", lineHeight: 1.05, color: "#1a2a7a" }}>
          {safe(doctor.name, s.doctor_name) || "DOCTOR NAME, MD"}
        </div>
        {safe(doctor.specialty, s.specialty) ? (
          <div style={{ fontSize: "9pt", marginTop: "1mm", color: "#0e7c7b", fontWeight: 600 }}>{safe(doctor.specialty, s.specialty)}</div>
        ) : null}
        <div className="text-[8pt] text-slate-600 mt-0.5">
          Lic. No.: {safe(doctor.license_number, s.license_number) || "—"} · PTR No.: {safe(doctor.ptr_number, s.ptr_number) || "—"}
        </div>
      </div>
      <div style={{ fontSize: "7.5pt", lineHeight: 1.3, color: "#0f172a" }}>
        {clinics.map((c, i) => (
          <div key={i} style={{ padding: "0.4mm 0", borderBottom: i < clinics.length - 1 ? "0.3pt dotted #cbd5e1" : "none" }}>
            <b>{safe(c.name)}{c.name ? ":" : ""}</b> {safe(c.schedule)}{c.contact ? ` CP: ${c.contact}` : ""}
          </div>
        ))}
      </div>
    </div>
  ) : (
    <div className="pb-3 border-b-2 border-slate-800">
      <div className="text-center">
        <div className="font-bold text-slate-900" style={{ fontFamily: "Work Sans, Arial, sans-serif", fontSize: "15pt", letterSpacing: "0.01em", lineHeight: 1.08 }}>
          {safe(doctor.name, s.doctor_name) || "DOCTOR NAME, MD"}
        </div>
        {(safe(doctor.specialty, s.specialty)) ? (
          <div className="text-slate-800 mt-0.5" style={{ fontFamily: "Arial, sans-serif", fontSize: "9pt", lineHeight: 1.15 }}>{safe(doctor.specialty, s.specialty)}</div>
        ) : null}
        <div className="text-[8pt] text-slate-600 mt-0.5">
          Lic. No.: {safe(doctor.license_number, s.license_number) || "—"} · PTR No.: {safe(doctor.ptr_number, s.ptr_number) || "—"}
        </div>
      </div>

      {affiliations.length > 0 ? (
        <div
          className="mt-2 text-[7pt] leading-[1.12] text-slate-900"
          style={{ fontFamily: "Arial, sans-serif", display: "grid", gridTemplateColumns: "1.05fr 0.95fr 1.1fr", columnGap: "8mm" }}
        >
          {affiliations.map((af, ai) => (
            <div key={ai}>
              {ai === 0 ? <div className="italic underline mb-0.5">Affiliations:</div> : null}
              <div className="font-bold uppercase">{af.name}</div>
              {(af.lines || []).map((ln, li) => {
                const isHospitalLine = /^[A-Z][A-Z .,&]+$/.test(ln) && ln.length > 6 && ln === ln.toUpperCase();
                return (
                  <div key={li} className={isHospitalLine ? "font-bold uppercase" : ""}>{ln}</div>
                );
              })}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="print-area" data-testid="referral-preview">
      <div className={`rx-paper allow-break ${paperCls} ${autoFit ? "auto-fit" : ""}`} style={{ ...pageStyle, position: "relative" }} data-print-label="Referral Letter">
        <RxWatermark doctor={doctor} settings={s} />
        {/* Table layout so the letterhead (thead) repeats on every printed page. */}
        <table className="rx-content" style={{ width: "100%", borderCollapse: "collapse", fontSize: "10.5pt", position: "relative", zIndex: 1 }}>
          <thead className="rx-repeat-header">
            <tr><td style={{ padding: "8mm 12mm 0 12mm" }}>{header}</td></tr>
          </thead>
          <tbody>
            <tr><td style={{ padding: "3mm 12mm 12mm 12mm", verticalAlign: "top" }}>

              <div className="text-center mt-1 mb-3">
                <div className="inline-block border-2 border-slate-800 px-6 py-1 uppercase font-bold tracking-[0.25em]" style={{ fontSize: "13pt" }}>
                  Referral Form
                </div>
              </div>

              <div className="flex items-baseline justify-between text-[10pt] mb-3">
                <div><span className="text-slate-500">Ref #:</span> <span className="mono font-medium">{doc.id ? doc.id.slice(0, 8).toUpperCase() : "—"}</span></div>
                <div><span className="text-slate-500">Date:</span> <span className="font-medium">{formatDate(doc.date)}</span></div>
              </div>

              {/* Patient block */}
              <div className="print-keep border border-slate-300 rounded-sm p-3 mb-3">
                <div className="text-[9pt] uppercase tracking-wider text-slate-500 font-semibold mb-1">Patient</div>
                <div className="grid grid-cols-2 gap-y-1">
                  <div><span className="text-slate-500 mr-2">Name:</span><span className="font-semibold">{safe(patient.full_name) || "—"}</span></div>
                  <div><span className="text-slate-500 mr-2">Age / Sex:</span><span>{safe(patient.age, "—")} / {safe(patient.gender, "—")}</span></div>
                  <div className="col-span-2"><span className="text-slate-500 mr-2">Address:</span>{safe(patient.address, "—")}</div>
                  <div className="col-span-2"><span className="text-slate-500 mr-2">Contact:</span><span className="mono">{safe(patient.contact_number, "—")}</span></div>
                </div>
              </div>

              {/* Referred to */}
              <div className="print-keep mb-3">
                <div className="text-[9pt] uppercase tracking-wider text-slate-500 font-semibold mb-1">Referred To</div>
                <div className="border-l-4 border-blue-700 pl-3">
                  <div className="font-bold text-base" style={{ fontFamily: "Work Sans" }}>{safe(doc.referred_to_name) || "—"}</div>
                  {doc.referred_to_specialty ? <div className="italic text-slate-700">{doc.referred_to_specialty}</div> : null}
                  {doc.referred_to_clinic ? <div className="text-slate-700">{doc.referred_to_clinic}</div> : null}
                  {doc.referred_to_contact ? <div className="text-slate-600 mono text-xs">{doc.referred_to_contact}</div> : null}
                </div>
              </div>

              {/* Reason */}
              <div className="print-keep mb-3">
                <div className="text-[9pt] uppercase tracking-wider text-slate-500 font-semibold mb-1">Reason for Referral</div>
                <div className="whitespace-pre-line text-slate-900 min-h-[8mm] border-b border-slate-300 pb-1">{safe(doc.reason_for_referral) || "—"}</div>
              </div>

              {/* Clinical summary (may flow to a second page — header repeats via thead) */}
              <div className="mb-3">
                <div className="text-[9pt] uppercase tracking-wider text-slate-500 font-semibold mb-1">Clinical Summary / History / Findings</div>
                <div className="whitespace-pre-line text-slate-900 min-h-[14mm] border border-slate-200 rounded-sm p-2">{safe(doc.clinical_summary) || ""}</div>
              </div>

              {doc.prescription_id ? (
                <div className="text-[9pt] text-slate-600 mb-3 italic">
                  ↳ Attached to Prescription — see accompanying Rx page for current medications.
                </div>
              ) : null}

              {/* Signature */}
              <div className="print-keep mt-4 flex items-end justify-end">
                <div className="text-right" style={{ width: "75mm" }}>
                  {(doctor.signature_image_url || s.signature_image_url) ? (
                    <img src={doctor.signature_image_url || s.signature_image_url} alt="signature" className="rx-signature ml-auto" style={{ maxHeight: "14mm", marginBottom: "-4px" }} />
                  ) : <div style={{ height: "12mm" }} />}
                  <div className="border-t border-slate-800 pt-1">
                    <div className="text-[10pt] font-bold uppercase">{safe(doctor.name, s.doctor_name) || "DOCTOR NAME, MD"}</div>
                    <div className="text-[9pt]">Lic. No.: {safe(doctor.license_number, s.license_number) || "—"}</div>
                    <div className="text-[9pt]">PTR No.: {safe(doctor.ptr_number, s.ptr_number) || "—"}</div>
                  </div>
                </div>
              </div>

            </td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
